import { useDropzone } from 'react-dropzone';
import { IpageSearch } from './image-popup';
import { AxiosInstance } from 'axios';
import {
  showNotification,
  StatusCode,
  toasterMessage,
} from '../../../page-builder/api/constant';
import { IAsset } from '../../../page-builder/interface/apiServiceInterface';
import pageBuilderService from '../../../page-builder/api/pageBuilderService';
import InputField from '../input-field';
import Button from '../button';
import { useState } from 'react';

type ImageProps = {
  setImageList: React.Dispatch<React.SetStateAction<IAsset[]>>;
  setDefaultImageList: React.Dispatch<React.SetStateAction<IAsset[]>>;
  setPagesSearch: React.Dispatch<React.SetStateAction<IpageSearch>>;
  storeID: string;
  baseService: AxiosInstance;
  clientID: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  width?: string;
  height?: string;
};

export const ImageInput = ({
  setImageList,
  setDefaultImageList,
  setPagesSearch,
  storeID,
  clientID,
  setLoading,
  baseService,
}: ImageProps) => {
  const [url, setURl] = useState({
    text: '',
    error: '',
  });

  const handleUrlUpload = async () => {
    try {
      if (!url?.text) {
        setURl(pre => ({
          ...pre,
          error: 'Please type the URL',
        }));
        return;
      }
      const files: FormData[] = [];

      const fileName = url?.text?.split('/')?.pop() || 'file'; // Extract the file name from the URL

      const formData = new FormData();
      formData.append('name', fileName);
      formData.append('storeID', storeID);
      formData.append('clientID', clientID);
      formData.append('fileName', fileName);
      formData.append('url', url?.text);
      try {
        const dimensions = await getImageDimensionsFromUrl(url?.text);
        formData.append('width', dimensions?.width?.toString());
        formData.append('height', dimensions?.height?.toString());
      } catch (error) {
        console.log('err', error);
      }
      files.push(formData);
      uploadImage(files);
    } catch (error) {
      console.log('err', error);
    }
  };

  const uploadImage = async (files: FormData[]) => {
    try {
      setLoading(true);
      const response = await pageBuilderService.createAsset(files, baseService);
      setLoading(false);
      if (response.status === 200) {
        showNotification(StatusCode.success, toasterMessage.imageUploadSuccess);
        setImageList([]);
        setDefaultImageList([]);
        setURl({
          text: '',
          error: '',
        });
        setPagesSearch(prevPage => ({
          ...prevPage,
          Page: 0,
        }));
      }
    } catch (error) {
      console.log('err', error);
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/svg+xml': ['.svg'],
      'video/mp4': ['.mp4'],
    },
    onDropRejected: fileRejections => {
      fileRejections.forEach(({ file, errors }) => {
        errors.forEach(({ code }) => {
          let errorMessage;
          switch (code) {
            case 'file-invalid-type':
              errorMessage =
                'Invalid format. Only JPEG, PNG, WEBP, and SVG files are allowed.';
              break;
            default:
              errorMessage = 'File not accepted.';
          }
          showNotification(
            StatusCode.failed,
            `File: ${file.name} - ${errorMessage}`,
          );
        });
      });
    },
    onDrop: async acceptedFiles => {
      try {
        if (acceptedFiles.length) {
          const files: FormData[] = [];
          for (const file of acceptedFiles) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', file.name);
            formData.append('fileName', file.name);
            formData.append('storeID', storeID);
            formData.append('clientID', clientID);
            if (file.type !== 'video/mp4') {
              const dimensions = await getImageDimensionsFromFile(file);
              formData.append('width', dimensions?.width?.toString());
              formData.append('height', dimensions?.height?.toString());
            }
            files.push(formData);
          }
          uploadImage(files);
        }
      } catch (error) {
        setLoading(false);
        console.error('ERROR IN onDropAccepted', error);
      }
    },
  });

  const getImageDimensionsFromFile = (
    file: File,
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(objectUrl); // cleanup
      };

      img.onerror = reject;

      img.src = objectUrl;
    });
  };

  const getImageDimensionsFromUrl = (
    url: string,
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();

      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = () => {
        const fallbackImg = new Image();
        fallbackImg.src =
          url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
        fallbackImg.onload = () => {
          resolve({
            width: fallbackImg.naturalWidth,
            height: fallbackImg.naturalHeight,
          });
        };
        fallbackImg.onerror = () => {
          reject(new Error('Failed to load image for dimension detection'));
        };
      };
      img.src = url;
    });
  };

  return (
    <div>
      <div className="flex items-center justify-center  p-[36px] border border-gray-400 border-dashed bg-white-50 cursor-pointer rounded-md">
        <section className="container flex justify-center">
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <p className="pl-6">Upload Image</p>
            <p>Drag and drop images</p>
          </div>
        </section>
      </div>
      <div className="flex gap-3 py-2">
        <div>
          <InputField
            value={url?.text}
            className="pagebuilder-theme"
            placeholder="Enter Image URL http://"
            onChange={e =>
              setURl({
                text: e,
                error: '',
              })
            }
          />
          {url?.error ? (
            <p className="text-red-600 text-xs px-2">{url?.error}</p>
          ) : (
            <></>
          )}
        </div>
        <Button
          label="Upload Image"
          variant="primary"
          onClick={handleUrlUpload}
        />
      </div>
    </div>
  );
};
